/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Alert,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useReportError } from "../../../hooks/useReportError";
import type {
  ExportFormat,
  ExportColumn,
  ExportDataProps
} from "../interfaces/exportData";

export type { ExportFormat, ExportColumn, ExportDataProps };

const ExportData = <T extends Record<string, any>>({
  data,
  columns,
  fetchDataForExport,
  fullDatasetExportHint,
  filename = "export",
  title,
  buttonVariant = "outlined",
  buttonText,
  formats = ["csv", "xlsx", "pdf", "json"],
  onExportStart,
  onExportComplete,
  onExportError
}: ExportDataProps<T>) => {
  const { t } = useKivunovaTranslation();
  const reportError = useReportError();
  const theme = useTheme();
  const displayTitle = title ?? t("export-data-title", "Export Data");
  const displayButtonText = buttonText ?? t("export-data-button", "Export");
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("csv");
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    columns.map((col) => String(col.key))
  );
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!isExporting) {
      setOpen(false);
      setError(null);
    }
  };

  const handleColumnToggle = (columnKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleSelectAllColumns = () => {
    if (selectedColumns.length === columns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columns.map((col) => String(col.key)));
    }
  };

  // Get value from nested object using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Format data based on selected columns
  const getFormattedData = (rows: T[]) => {
    const activeColumns = columns.filter((col) =>
      selectedColumns.includes(String(col.key))
    );

    return rows.map((row) => {
      const formattedRow: Record<string, any> = {};
      activeColumns.forEach((col) => {
        const value = getNestedValue(row, String(col.key));
        formattedRow[col.label] = col.formatter
          ? col.formatter(value, row)
          : (value ?? "N/A");
      });
      return formattedRow;
    });
  };

  // Export to CSV
  const exportToCSV = (rows: T[]) => {
    const formattedData = getFormattedData(rows);
    const headers = Object.keys(formattedData[0] || {});

    const csvContent = [
      headers.join(","),
      ...formattedData.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
          })
          .join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    downloadFile(blob, `${filename}.csv`);
  };

  // Export to JSON
  const exportToJSON = (rows: T[]) => {
    const formattedData = getFormattedData(rows);
    const jsonContent = JSON.stringify(formattedData, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    downloadFile(blob, `${filename}.json`);
  };

  // Export to XLSX
  const exportToXLSX = (rows: T[]) => {
    const formattedData = getFormattedData(rows);
    const headers = Object.keys(formattedData[0] || {});

    const xlsxContent = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Sheet1">
  <Table>
   <Row>
    ${headers.map((h) => `<Cell><Data ss:Type="String">${h}</Data></Cell>`).join("")}
   </Row>
   ${formattedData
     .map(
       (row) => `
   <Row>
    ${headers.map((h) => `<Cell><Data ss:Type="String">${row[h]}</Data></Cell>`).join("")}
   </Row>`
     )
     .join("")}
  </Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob([xlsxContent], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    downloadFile(blob, `${filename}.xml`);
  };

  // Export to PDF
  const exportToPDF = (rows: T[]) => {
    const formattedData = getFormattedData(rows);
    const headers = Object.keys(formattedData[0] || {});

    const tableHTML = `
      <html>
        <head>
          <title>${displayTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #4CAF50; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>${displayTitle}</h1>
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${formattedData
                .map(
                  (row) => `
                <tr>${headers.map((h) => `<td>${row[h]}</td>`).join("")}</tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([tableHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(url);
      };
    }
  };

  // Download file helper
  const downloadFile = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Main export handler
  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      setError("Please select at least one column to export");
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      onExportStart?.();

      const rows = fetchDataForExport ? await fetchDataForExport() : data;

      // Add a small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 500));

      switch (selectedFormat) {
        case "csv":
          exportToCSV(rows);
          break;
        case "xlsx":
          exportToXLSX(rows);
          break;
        case "pdf":
          exportToPDF(rows);
          break;
        case "json":
          exportToJSON(rows);
          break;
        default:
          throw new Error("Unsupported export format");
      }

      onExportComplete?.(selectedFormat, rows.length);
      handleClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      reportError("Export data failed", err, { component: "ExportData" });
      setError(error.message || "Export failed");
      onExportError?.(error);
    } finally {
      setIsExporting(false);
    }
  };

  const formatLabels: Record<ExportFormat, string> = {
    csv: "CSV (Comma Separated Values)",
    xlsx: "Excel Spreadsheet",
    pdf: "PDF Document",
    json: "JSON File"
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={handleOpen}
        startIcon={<FileDownloadOutlinedIcon />}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          ...(buttonVariant === "outlined" && {
            borderColor: BRAND_COLOR,
            color: BRAND_COLOR,
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              color: "white",
              borderColor: BRAND_HOVER
            }
          }),
          ...(buttonVariant === "contained" && {
            backgroundColor: BRAND_COLOR,
            "&:hover": { backgroundColor: BRAND_HOVER }
          })
        }}
      >
        {displayButtonText}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh"
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {displayTitle}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isExporting}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555"
              }
            }
          }}
        >
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Export Format Section */}
          <Box sx={{ mb: matchMD ? 3 : 4 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              fontWeight={600}
              color="textPrimary"
            >
              Select Export Format
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedFormat}
                onChange={(e) =>
                  setSelectedFormat(e.target.value as ExportFormat)
                }
              >
                {formats.map((format) => (
                  <FormControlLabel
                    key={format}
                    value={format}
                    control={
                      <Radio
                        sx={{
                          color: BRAND_COLOR,
                          "&.Mui-checked": { color: BRAND_COLOR }
                        }}
                      />
                    }
                    label={formatLabels[format]}
                    disabled={isExporting}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Column Selection Section */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="textPrimary"
              >
                Select Columns to Export
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAllColumns}
                disabled={isExporting}
                sx={{
                  textTransform: "none",
                  color: BRAND_COLOR,
                  "&:hover": {
                    backgroundColor: `${BRAND_COLOR}10`
                  }
                }}
              >
                {selectedColumns.length === columns.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Box>
            <FormGroup>
              {columns.map((col) => (
                <FormControlLabel
                  key={String(col.key)}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(String(col.key))}
                      onChange={() => handleColumnToggle(String(col.key))}
                      disabled={isExporting}
                      sx={{
                        color: BRAND_COLOR,
                        "&.Mui-checked": { color: BRAND_COLOR }
                      }}
                    />
                  }
                  label={col.label}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Record count / export scope */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            {fetchDataForExport
              ? (fullDatasetExportHint ??
                "Export will load all orders matching the current filters (for example status), then apply your search and sort — not only the rows on this page.")
              : `${data.length} record${data.length !== 1 ? "s" : ""} will be exported`}
          </Typography>
        </Box>

        {/* Fixed Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            variant="contained"
            loading={isExporting}
            onClick={handleExport}
            disabled={selectedColumns.length === 0}
            fullWidth
            size={matchMD ? "medium" : "large"}
            startIcon={!isExporting ? <FileDownloadOutlinedIcon /> : undefined}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {isExporting ? "Exporting..." : "Export"}
          </LoadingButton>

          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isExporting}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default ExportData;
